import { AlertTriangle, Bell, BellRing, Check, CheckCircle2, Info, LoaderCircle, XCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import { useNotifications } from "@/hooks/useNotifications";
import { cn } from "@/lib/utils";
import type { Notification } from "@/services/notifications.service";

const notificationIcons = {
  info: Info,
  sucesso: CheckCircle2,
  alerta: AlertTriangle,
  erro: XCircle,
};

function NotificationItem({ notification, onOpen }: { notification: Notification; onOpen: () => void }) {
  const Icon = notificationIcons[notification.tipo as keyof typeof notificationIcons] ?? Info;
  return (
    <DropdownMenuItem className="items-start gap-3 px-3 py-3" onSelect={onOpen}>
      <span className={cn("mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg", notification.lida ? "bg-muted text-muted-foreground" : "bg-primary/10 text-primary")}>
        <Icon className="size-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-start gap-2">
          <strong className="line-clamp-1 flex-1 text-xs font-semibold">{notification.titulo}</strong>
          {!notification.lida && <span className="mt-1 size-2 shrink-0 rounded-full bg-primary" />}
        </span>
        <span className="mt-0.5 line-clamp-2 text-[11px] leading-relaxed text-muted-foreground">{notification.mensagem}</span>
        <small className="mt-1 block text-[10px] text-muted-foreground/75">
          {formatDistanceToNow(new Date(notification.criada_em), { addSuffix: true, locale: ptBR })}
        </small>
      </span>
    </DropdownMenuItem>
  );
}

export function NotificationsMenu() {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const notifications = useNotifications(Boolean(usuario?.id));
  const items = notifications.data ?? [];
  const unread = items.filter((item) => !item.lida).length;

  function openNotification(notification: Notification) {
    if (!notification.lida) notifications.markAsRead.mutate(notification.id);
    if (notification.link?.startsWith("/")) navigate(notification.link);
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className="relative" size="icon" variant="ghost" aria-label={unread ? `${unread} notificações não lidas` : "Notificações"}>
          {unread ? <BellRing className="size-[18px]" /> : <Bell className="size-[18px]" />}
          {unread > 0 && <span className="absolute right-1 top-1 grid min-w-4 place-items-center rounded-full bg-primary px-1 text-[9px] font-bold leading-4 text-primary-foreground">{unread > 9 ? "9+" : unread}</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[min(360px,calc(100vw-24px))] p-1.5">
        <div className="flex items-center justify-between px-3 py-2">
          <div><p className="text-sm font-semibold">Notificações</p><p className="text-[10px] text-muted-foreground">Atualizações importantes do sistema</p></div>
          {unread > 0 && (
            <Button size="sm" variant="ghost" className="h-8 px-2 text-[11px]" disabled={notifications.markAllAsRead.isPending} onClick={() => notifications.markAllAsRead.mutate()}>
              {notifications.markAllAsRead.isPending ? <LoaderCircle className="size-3 animate-spin" /> : <Check className="size-3" />}Marcar lidas
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {notifications.isLoading ? (
            <div className="flex items-center justify-center gap-2 px-4 py-10 text-xs text-muted-foreground"><LoaderCircle className="size-4 animate-spin" />Carregando...</div>
          ) : notifications.isError ? (
            <div className="m-2 rounded-lg bg-destructive/8 px-3 py-5 text-center text-xs text-destructive">Não foi possível carregar as notificações.</div>
          ) : items.length ? (
            items.map((notification) => <NotificationItem key={notification.id} notification={notification} onOpen={() => openNotification(notification)} />)
          ) : (
            <div className="m-2 rounded-lg bg-muted/60 px-3 py-8 text-center text-xs text-muted-foreground"><Bell className="mx-auto mb-2 size-5 opacity-50" />Nenhuma notificação no momento.</div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
